import React, { useState } from "react";
import "react-data-grid/lib/styles.css";
import { DataGrid } from "react-data-grid";
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    MenuItem,
    Select,
    Collapse,
    Chip,
    Stack
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { CalculateResponse } from '../../../api/LVCCApi';

const TwoPoint = () => {
    const [bgOpen, setBgOpen] = useState(false);
    const [codeOpen, setCodeOpen] = useState(false);
    const [gridData, setGridData] = useState([]);
    const [response, setResponse] = useState("{}");

    const [columns, setColumns] = useState([
        { key: "col0", name: "Paste Data Here..." },
        { key: "col1", name: "" },
        { key: "col2", name: "" },
        { key: "col3", name: "" },
        { key: "col4", name: "" },
        { key: "col5", name: "" },
    ]);

    const blankRows = Array(6).fill({}).map((_, index) => ({
        id: index + 1,
        col0: "",
        col1: "",
        col2: "",
        col3: "",
        col4: ""
    }));

    const handlePaste = (event) => {
        event.preventDefault();
        const clipboardData = event.clipboardData.getData("text");
        const rows = clipboardData.trim().split("\n").map((row) => row.split("\t").map((cell) => cell.trim()));

        if (rows.length > 0) {
            const minColumns = 6;
            const minRows = 6;
            const newColumns = Array.from({ length: minColumns }, (_, index) => ({
                key: `col${index}`,
                name: rows[0][index] || "",
            }));

            const newData = rows.slice(1).map((row, rowIndex) => {
                let rowData = { id: rowIndex + 1 };
                for (let i = 0; i < minColumns; i++) {
                    rowData[`col${i}`] = row[i] || "";
                }
                return rowData;
            });

            while (newData.length < minRows) {
                let emptyRow = { id: newData.length + 1 };
                for (let i = 0; i < minColumns; i++) {
                    emptyRow[`col${i}`] = "";
                }
                newData.push(emptyRow);
            }

            setColumns(newColumns);
            setGridData(newData);
        }
    };

    const [parameters, setParameters] = useState({
        assayNumber: "300",
        wcf: "0.9894",
        bcf: "0",
        predictTime: "330.27",
        readTime: "",
        signal: "",
        reference: "",
        dark: "",
    });

    const assayOptions = ["300", "301", "302", "303", "304"];

    const handleCalculate = () => {
        const getColumnValues = (columnName) => {
            const columnKey = columns.find(col => col.name === columnName)?.key;
            return columnKey ? gridData.map(row => row[columnKey]).filter(val => val !== undefined && val !== "") : [];
        };

        const apiPayload = {
            assayNumber: parameters.assayNumber,
            wcf: [parseFloat(parameters.wcf)],
            bcf: [parseFloat(parameters.bcf)],
            readTime: getColumnValues(parameters.readTime),
            signal: getColumnValues(parameters.signal),
            reference: getColumnValues(parameters.reference),
            dark: getColumnValues(parameters.dark),
            genInfo: {
                lAssayNumber: parameters.assayNumber,
                fPredictTime1: parameters.predictTime
            },
            ppspline: {  lAssayNumber: 300,
                sSONumber: 50,
                eBodyFluid: 5,
                sGen: 30,
                fTransformX_1: 0.0,
                fTransformX_2: 0.0,
                fTransformX_3: 0.0,
                fTransformX_4: 0.0,
                fTransformX_5: 0.0,
                fTransformX_6: 0.0,
                fTransformX_7: 0.0,
                fTransformX_8: 0.0,
                fTransformX_9: 0.0,
                fTransformX_10: 0.0,
                fTransformY_1: 0.0,
                fTransformY_2: 0.0,
                fTransformY_3: 0.0,
                fTransformY_4: 0.0,
                fTransformY_5: 0.0,
                fTransformY_6: 0.0,
                fTransformY_7: 0.0,
                fTransformY_8: 0.0,
                fTransformY_9: 0.0,
                fTransformY_10: 0.0,
                fTransformY2_1: 0.0,
                fTransformY2_2: 0.0,
                fTransformY2_3: 0.0,
                fTransformY2_4: 0.0,
                fTransformY2_5: 0.0,
                fTransformY2_6: 0.0,
                fTransformY2_7: 0.0,
                fTransformY2_8: 0.0,
                fTransformY2_9: 0.0,
                fTransformY2_10: 0.0,
                sTransformNodeCount: 0},
        };

        console.log("API Call Payload:", JSON.stringify(apiPayload, null, 2));
        CalculateResponse.TP(apiPayload).then((r) => {
            console.log(r);
            setResponse(JSON.stringify(r.data));
        });
        // setResponse(JSON.stringify(apiPayload, null, 2));
    };

    return (
        <Box sx={{ pl: 3, pr: 3, pb: 3 }}>
            <Box sx={{ mb: 2 }}>
                <Button onClick={() => setBgOpen(!bgOpen)} startIcon={bgOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
                    Background
                </Button>
                <Collapse in={bgOpen}>
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Typography variant="body1">
                            The endpoint method calculates the reflectance at each read time...
                        </Typography>
                    </Paper>
                </Collapse>
            </Box>

            <Typography variant="h6">Data</Typography>
            <Typography variant="subtitle2">Copy and Paste data below. Data must include column headers.</Typography>
            <Box
                sx={{
                    minHeight: 100,
                    maxHeight: '247px',
                    border: "1px solid lightgrey",
                    borderRadius: 1,
                    overflowY: "auto",
                    mb: 2,
                    mt: 2
                }}
                onPaste={handlePaste}
            >
                <DataGrid autoHeight columns={gridData.length ? columns : columns} rows={gridData.length ? gridData : blankRows} />
            </Box>

            <Typography variant="h6" sx={{ mb: 1, pt: 2 }}>Parameters</Typography>
            <Box
                className="param"
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 2
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <Typography variant="caption" sx={{ fontWeight: "bold", color: "gray" }}>
                        ASSAY NUMBER
                    </Typography>
                    <Select
                        value={parameters.assayNumber}
                        onChange={(e) => setParameters({ ...parameters, assayNumber: e.target.value })}
                        fullWidth
                        displayEmpty
                        sx={{
                            fontSize: "0.875rem",
                            padding: "0px",
                            minHeight: "32px!important",
                            backgroundColor: "white",
                        }}
                    >
                        {assayOptions.map((assay) => (
                            <MenuItem key={assay} value={assay}>{assay}</MenuItem>
                        ))}
                    </Select>
                </Box>

                {Object.entries(parameters).map(([key, value]) => (
                    key !== "assayNumber" && (
                        <Box key={key} sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", color: "gray" }}>
                                {key.toUpperCase()}
                            </Typography>
                            {["wcf", "bcf", "predictTime"].includes(key) ? (
                                <TextField
                                    type="number"
                                    value={value}
                                    onChange={(e) => setParameters({ ...parameters, [key]: e.target.value })}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                />
                            ) : (
                                <Select
                                    value={value}
                                    onChange={(e) => setParameters({ ...parameters, [key]: e.target.value })}
                                    displayEmpty
                                    fullWidth
                                    sx={{
                                        fontSize: "0.875rem",
                                        padding: "0px",
                                        minHeight: "32px!important",
                                        backgroundColor: "white",
                                    }}
                                >
                                    <MenuItem value="">{key.toUpperCase()}</MenuItem>
                                    {columns.map(col => (
                                        <MenuItem key={col.key} value={col.name}>{col.name}</MenuItem>
                                    ))}
                                </Select>
                            )}
                        </Box>
                    )
                ))}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "right", mb: 3, pr: 5 }}>
                <Button variant="contained" color="primary" size="large" onClick={handleCalculate}>Calculate</Button>
            </Box>

            <Typography variant="h6">Output</Typography>
            <TextField multiline rows={6} value={response} fullWidth margin="dense" />

            <Button onClick={() => setCodeOpen(!codeOpen)} startIcon={codeOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
                Code
            </Button>
            <Collapse in={codeOpen}>
                <Paper sx={{ p: 2 }}>Java Code Goes In Here...</Paper>
            </Collapse>
        </Box>
    );
};

export default TwoPoint;